import { CircleIcon } from '@assets/icons';
import { Tooltip } from '@components/tooltip';
import { type Editor } from '@generated/graphql';
import { Box, useTheme, type SxProps } from '@mui/material';
import { formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { ServerTag } from '../ServerTag';

export const ServerStatus = ({
  status,
  startedAt,
  variant = 'full',
  sx,
}: {
  status: Editor['status'];
  startedAt?: Editor['startedAt'];
  variant?: 'full' | 'icon';
  sx?: SxProps;
}) => {
  const { palette } = useTheme();
  const [uptime, setUptime] = useState<string | null>(null);

  useEffect(() => {
    if (status?.state !== 'running' || !startedAt) {
      setUptime(null);
      return () => {};
    }

    const computeUptime = () => {
      const parsedDate = parseISO(startedAt);
      if (!isValid(parsedDate)) return null;
      return formatDistanceToNowStrict(parsedDate);
    };

    setUptime(computeUptime());
    const interval = setInterval(() => setUptime(computeUptime()), 30_000);
    return () => clearInterval(interval);
  }, [startedAt, status?.state]);

  if (variant === 'icon') {
    const indicatorColor =
      status.state.toLowerCase() === 'running'
        ? palette.success.main
        : status.state.toLowerCase() === 'stopped'
          ? palette.error.main
          : palette.warning.main;
    return (
      <Tooltip title={status?.state}>
        <Box sx={sx}>
          <CircleIcon color={indicatorColor} size={12} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <ServerTag
      label={status?.state === 'running' ? `Running (${uptime})` : status?.state}
      variant="caption2"
      color={status?.state.toLowerCase() === 'running' ? 'success' : 'default'}
      sx={{ textTransform: 'capitalize', whiteSpace: 'nowrap', ...sx }}
    />
  );
};
